/**
 * Home's part of the FTUX's Spotlight: the waiting piece's card as one ImportantNow notice, carrying Lumio's pick as
 * the Spotlight draws it. Eager (Home calls it for every owner), so it reads the slice only through mounted-guarded
 * selectors and moves the run only through the store's transitions: no layer, no GSAP, no lazy FTUX module.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { SpotlightPickItem } from '@/nova/pages/home/components/Spotlight';
import type { ImportantNotice } from '@/nova/pages/home/components/ImportantNow/useImportantNotices';
import { ROUTE } from '@/nova/routes';
import { useAppDispatch, useAppSelector, type RootState } from '@/store';

import type { BrandPieceId } from '../catalog';
import { ftuxEvent } from '../store/ftuxEvent';
import { selectFtuxSpotlightPick } from '../store/selectors';
import { ftuxDismissPick, ftuxOpenIntro } from '../store/transitions';
import type { FtuxTargetId } from '../targets';

/** Where the FTUX finds the card: Lumio's light laps it while it waits (the layer's WaitingShine). */
const PICK_TARGET: FtuxTargetId = 'spotlight-pick';

/** The run the card on show belongs to, or null while none shows (selectFtuxSpotlightPick needs the layer mounted). */
const selectPickRun = (state: RootState): number | null =>
  selectFtuxSpotlightPick(state) ? state.ftux.progress?.run ?? null : null;

const NO_NOTICES: readonly ImportantNotice[] = [];

/**
 * Reports the card as shown once per Home visit (Home holds this hook for as long as the owner stays): the card coming
 * back after the piece's pop-up was closed sends nothing new, and neither does a brand's card coming back after a
 * switch to another brand and back (each run remembers its last card); another piece's card or a new run's does.
 */
const useShownOncePerVisit = (piece: BrandPieceId | null, run: number | null): void => {
  const dispatch = useAppDispatch();
  const sent = React.useRef(new Map<number, BrandPieceId>());
  React.useEffect(() => {
    if (piece === null || run === null) return;
    if (sent.current.get(run) === piece) return;
    sent.current.set(run, piece);
    dispatch(ftuxEvent({ type: 'shown', piece, step: 'spotlight-pick' }));
  }, [dispatch, piece, run]);
};

/**
 * The waiting piece's card for Home's Spotlight: one `ftux-pick` notice while a piece waits and can be offered
 * (nothing open but its own pop-up, its availability ready, the brand fully deployed with no deployment strip on Home)
 * and the owner is on Home; otherwise none. Home leads its notices with it. Its pick is "Getting started" over the piece's title, its line
 * (why it is worth the owner's minute, then what Lumio offers), the button that opens the piece's pop-up and a quiet
 * Dismiss, which lets the piece go so the next one's card comes (after the last, the run ends). Both report their
 * click themselves (the store's transitions). Each piece's card is a new one in the strip, so it arrives fresh.
 */
export const useFtuxSpotlight = (): readonly ImportantNotice[] => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const meta = useAppSelector(selectFtuxSpotlightPick);
  const run = useAppSelector(selectPickRun);
  const waiting = meta && pathname === ROUTE.home() ? meta : null;
  useShownOncePerVisit(waiting ? waiting.id : null, run);
  const onOpen = React.useCallback(() => {
    dispatch(ftuxOpenIntro());
  }, [dispatch]);
  const onDismiss = React.useCallback(() => {
    dispatch(ftuxDismissPick());
  }, [dispatch]);
  return React.useMemo((): readonly ImportantNotice[] => {
    if (waiting === null) return NO_NOTICES;
    const { title, offer, cta } = waiting.pick;
    const pick: SpotlightPickItem = {
      id: `ftux-pick-${waiting.id}`,
      variant: 'pick',
      title,
      body: offer,
      cta,
      ariaLabel: t("Lumio's pick: {{title}}", { title: t(title) }),
      onActivate: onOpen,
      onDismiss,
      target: PICK_TARGET,
      fresh: true,
    };
    return [{ kind: 'ftux-pick', tone: 'lumio', ftuxPick: pick }];
  }, [waiting, t, onOpen, onDismiss]);
};
